<template>
  <Modal class="loan-details-modal" :closeBtn="true" @close="closeModal">
    <circle-loader v-if="gettingData" :inline="true"></circle-loader>
    <template v-else>
      <refi-program-loan-options />
      <dialogue-modal
        v-if="showDialogueModal === true"
        @cancel="showDialogueModal = false"
        @confirm="handleDialogueModal"
        :title="`Quit editing?`"
        :msg="`Any changes you\'ve made so far will not be saved`"
        :confirmButtonText="'Yes'"
        :cancelButtonText="'No'"
      />
    </template>
    <template v-slot:footer>
      <div v-if="updateLoanConfigurationError" class="error">
        {{ updateLoanConfigurationErrorText }}
      </div>
      <div class="step-btns">
        <next-button
          @click.prevent="cancelChanges"
          text="Cancel"
          :loading="discardingChanges"
          :disabled="savingChanges || discardingChanges"
        >
        </next-button>
        <next-button
          @click.prevent="confirmChanges"
          text="Save Changes"
          :loading="savingChanges"
          :disabled="savingChanges || discardingChanges"
        >
        </next-button>
      </div>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import { ref, defineEmits, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import DialogueModal from "@/components/modals/DialogueModal.vue";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import RefiProgramLoanOptions from "@/components/refi-loan-configuration/RefiProgramLoanOptions.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";

const store = useStore();

const emit = defineEmits(["close"]);

const showDialogueModal = ref<boolean>(false);

const gettingData = ref<boolean>(true);
const savingChanges = ref<boolean>(false);
const discardingChanges = ref<boolean>(false);

const updatingLoanConfiguration = computed<boolean>(
  () => store.state.refiLoanConfigurationModule.updatingLoanConfiguration,
);
const updateLoanConfigurationError = computed<boolean>(
  () => store.state.refiLoanConfigurationModule.updateLoanConfigurationError,
);
const updateLoanConfigurationErrorText = computed<boolean>(
  () => store.state.refiLoanConfigurationModule.updateLoanConfigurationErrorText,
);

function openDialogueModal() {
  showDialogueModal.value = true;
}
function handleDialogueModal() {
  store.dispatch("applicationInfoModule/discardChanges");
  store.dispatch("applicationInfoModule/closeModal", "InterestRateAndLoanTerm");
}
function closeModal() {
  openDialogueModal();
}
function cancelChanges() {
  discardingChanges.value = true;
  handleDialogueModal();
}
async function confirmChanges() {
  savingChanges.value = true;
  const response = await store.dispatch("refiLoanConfigurationModule/updateLoanConfiguration");

  savingChanges.value = response;

  if (response) {
    // Get latest loan details
    await store.dispatch(
      "applicationInfoModule/getRefiDetails",
      store.state?.appData?.application?.id,
    );

    // Close
    store.dispatch("applicationInfoModule/closeModal", "InterestRateAndLoanTerm");
  }
}

async function getRefiRatePlans() {
  discardingChanges.value = false;
  savingChanges.value = false;
  await store.dispatch(
    "refiLoanConfigurationModule/getRefiRatePlans",
    store?.state?.appData?.application?.id,
  );
  gettingData.value = false;
}

onMounted(() => {
  store.dispatch("refiLoanConfigurationModule/resetRefiLoanConfigurationModal");
  getRefiRatePlans();
});
watch(
  () => updateLoanConfigurationError.value,
  (value: boolean) => {
    if (value) {
      savingChanges.value = false;
    }
  },
);
</script>

<style scoped>
.error {
  color: var(--sc-red);
  text-align: center;
  font-weight: 700;
}

.step-btns {
  display: flex;
  gap: 12px;
	flex-wrap: wrap;
}
</style>
